<template>
  <StepLayout :tracking="trackingStep">
    <template #left>
      <div class="items-baseline justify-between md:hidden">
        <h2 class="heading-1">
          {{ step.title }}
        </h2>
      </div>
      <StepIllustration
        v-if="step.desktopIllustration"
        class="mb-24 mt-16"
        :position="step.desktopIllustration.position"
        :src="step.desktopIllustration.src"
      />
    </template>

    <template #right>
      <div class="mb-16 hidden items-baseline justify-between md:flex">
        <h2 class="heading-1">
          {{ step.title }}
        </h2>
      </div>

      <RevInfoBlock
        v-if="defaultKeyboard?.label !== selectedKeyboard?.label"
        class="mb-24"
        :icon="IconWarning"
        :title="i18n(translations.warning)"
        variant="warning"
      />

      <ul class="list-none">
        <li v-if="defaultKeyboard" class="mb-12">
          <LargePicker
            class="m-auto h-full"
            :index="-2"
            :label="defaultKeyboard.label as string"
            :loading="loading && loadingIndex === -2"
            :price="i18n.price(defaultKeyboard.price ?? '')"
            :selected="defaultKeyboard.selected"
            :sub-label="defaultKeyboard.subLabel as string"
            @click="onOptionClick(defaultKeyboard, -2)"
          />
        </li>
        <li
          v-if="
            selectedKeyboard &&
            defaultKeyboard?.label !== selectedKeyboard.label
          "
          class="mb-12"
        >
          <LargePicker
            class="m-auto h-full"
            :index="-1"
            :label="selectedKeyboard.label as string"
            :loading="loading && loadingIndex === -1"
            :price="i18n.price(selectedKeyboard.price ?? '')"
            :selected="selectedKeyboard.selected"
            :sub-label="selectedKeyboard.subLabel as string"
            @click="onOptionClick(selectedKeyboard, -1)"
          />
        </li>
        <RevAccordionItem
          v-if="optionsWithoutDefaultAndSelected.length > 1"
          :wrapper-attrs="{ class: 'hidden md:block border rounded-sm' }"
        >
          <template #title>
            <span class="body-1">
              {{ i18n(translations.showMore) }}
            </span>
          </template>
          <template #body>
            <ul>
              <li
                v-for="(option, index) in optionsWithoutDefaultAndSelected"
                :key="option.label as string"
                class="mb-12 last:mb-0"
              >
                <LargePicker
                  class="m-auto h-full"
                  :index
                  :label="option.label as string"
                  :loading="loading && loadingIndex === index"
                  :price="i18n.price(option.price ?? '')"
                  :selected="option.selected"
                  @click="onOptionClick(option, index)"
                />
              </li>
            </ul>
          </template>
        </RevAccordionItem>
        <li
          v-if="optionsWithoutDefaultAndSelected.length > 1"
          class="md:hidden"
        >
          <RevButton
            full-width="always"
            variant="secondary"
            @click="openModal(MODAL_NAMES.KEYBOARD)"
          >
            {{ i18n(translations.showMore) }}
          </RevButton>
        </li>
      </ul>
      <KeyboardModal
        :default-keyboard
        :loading
        :loading-index
        :options="optionsWithoutDefaultAndSelected"
        :selected-keyboard
        @click="onOptionClick"
      />
    </template>
  </StepLayout>
</template>

<script lang="ts" setup>
import { navigateTo } from '#imports'
import { computed, ref } from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevButton } from '@ds/components/Button'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { openModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import { useCreateLinkFromPicker } from '~/scopes/product/composables/useCreateLinkFromPicker'
import { MODAL_NAMES } from '~/scopes/product/constants'

import type { Step } from '../../utils/types'
import LargePicker from '../LargePicker/LargePicker.vue'
import StepIllustration from '../StepIllustration/StepIllustration.vue'
import StepLayout from '../StepLayout/StepLayout.vue'

import translations from './StepKeyboard.translations'
import KeyboardModal from './components/KeyboardModal/KeyboardModal.vue'

const props = defineProps<{
  step: Step
  product: GetProductResponse
}>()

const i18n = useI18n()
const { createLink } = useCreateLinkFromPicker()
const { trackClick } = useTracking()

const loading = ref(false)
const loadingIndex = ref<number | null>(null)

const trackingStep = computed(() => {
  return {
    trackingTags: {
      tagDisplayed: 'no_tag',
      tagSelected: false,
    },
    trackingId: 'keyboard_picker',
    trackingModel: props.product.model,
    trackingCategory: props.product.tracking.categoryName,
  }
})

const defaultKeyboard = computed(() => {
  return props.step.options.find((option) => option.defaultForMarket)
})

const optionsWithoutDefaultAndSelected = computed(() => {
  return props.step.options.filter(
    (option) => !option.defaultForMarket && !option.selected,
  )
})

const selectedKeyboard = computed(() => {
  return props.step.options.find((option) => option.selected)
})

async function onOptionClick(option: Step['options'][number], index: number) {
  if (!option.available || loading.value) {
    return false
  }

  const label =
    typeof option.label === 'string' ? option.label : i18n(option.label)

  // If the field is already selected, we won't change the URL nor refresh the network calls, so
  // let's directly trigger the changed event and skip the loading state
  if (!option.selected) {
    loading.value = true
    loadingIndex.value = index
  }

  trackClick({
    zone: props.step.trackingId ?? 'unknown_picker',
    name: option.trackingValue ?? label,
  })

  // Update current URL
  const link = createLink(option, props.step)
  if (link) {
    await navigateTo(link)
  }

  return true
}
</script>
